import React from 'react';
import { Box, Chip, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';

const STATUS_COLOR = { correct: 'success', partial: 'warning', missed: 'error' };
const STATUS_LABEL = { correct: 'Correct', partial: 'Partial', missed: 'Needs work' };

// `grade` is the /practice/attempts/{id}/grade response shape — the attempt row
// (score, max_score) plus grade_result (mode, percent, lensResults, feedback)
// from practice_grading.py's normalized output.
const PracticeGradeResult = ({ grade, sx }) => {
  const theme = useTheme();
  const result = grade?.grade_result || {};
  const lensResults = result.lensResults || {};
  const steps = Object.entries(lensResults);
  const percent = result.percent ?? (grade?.max_score ? Math.round((grade.score / grade.max_score) * 100) : 0);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 2, borderColor: alpha(theme.palette.primary.main, 0.25), ...sx }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={800}>
          Practice score: {grade?.score ?? 0} / {grade?.max_score ?? 0} ({percent}%)
        </Typography>
        {result.mode === 'rules' && (
          <Chip size="small" label="Rule-based grading" variant="outlined" sx={{ fontSize: '0.65rem' }} />
        )}
      </Stack>

      {result.feedback && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {result.feedback}
        </Typography>
      )}

      <Stack spacing={1}>
        {steps.map(([key, step]) => (
          <Box key={key} sx={{ pl: 1.5, borderLeft: 3, borderColor: `${STATUS_COLOR[step.status] || 'grey'}.main` }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" fontWeight={700}>{step.label || key}</Typography>
              <Chip
                size="small"
                label={STATUS_LABEL[step.status] || step.status || 'Ungraded'}
                color={STATUS_COLOR[step.status] || 'default'}
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
              />
            </Stack>
            {step.reason && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {step.reason}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>

      {result.llm_error && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5 }}>
          AI grading was unavailable, so this used the rule-based fallback grader instead.
        </Typography>
      )}
    </Paper>
  );
};

export default PracticeGradeResult;
